import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Link as MUILink,
  Checkbox,
  IconButton
} from "@mui/material";
import { CompanySearchResults, Phone, SearchData } from "../types";
import { CustomAvatar } from "../../components/custom-avatar";
import { Link } from "react-router-dom";
import { getLogo } from "../../utils/getLogo";
import Highlighter from "react-highlight-words";
import LoadingSkeleton from "./LoadingSkeleton";
import { formatPhone } from "../shared/phones/formatPhone";
import { logEvent } from "../../utils/analyticsLogger";
import { Dispatch, SetStateAction } from "react";
import StickyTable from "../../shared/StickyTable";
import useResponsive from "../../../minimals/hooks/useResponsive";
import Iconify from "../../../minimals/components/iconify";

// Matches the individuals list, so a phone number looks the same on both tabs.
const IOS_GREEN = "#34C759";

interface IProps {
  companiesSearchResults: CompanySearchResults | undefined;
  searchData: SearchData;
  handlePageNumberChange: (number: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  searchWords: string[];
  isLoading: boolean;
  companiesToMerge: Set<number>;
  setCompaniesToMerge: Dispatch<SetStateAction<Set<number>>>;
  maxCompaniesToMerge: number;
  /** Mobile only: rows show a checkbox and tap-to-select instead of navigating. */
  isSelectionMode?: boolean;
}

const highlightStyles = { backgroundColor: "#F8FF00" };

export default function Companies(props: IProps) {
  const {
    companiesSearchResults,
    searchData,
    handlePageNumberChange,
    handleChangeRowsPerPage,
    searchWords,
    isLoading,
    companiesToMerge,
    setCompaniesToMerge,
    maxCompaniesToMerge,
    isSelectionMode = false
  } = props;

  // Seven columns don't fit a phone, so mobile renders the same rows as stacked
  // cards. Desktop keeps the table untouched.
  const isMobile = useResponsive("down", "md");
  // See Contacts.tsx: keep existing rows while a new search runs, so the page height
  // doesn't swing between results and a 10-row skeleton on every keystroke.
  const hasResults = Boolean(companiesSearchResults?.data?.length);
  const isRefreshing = isLoading && hasResults;
  const showSkeleton = isLoading && !hasResults;

  const handleCheckboxChange = (companyId: number, isChecked: boolean) => {
    if (isChecked) {
      addCompany(companyId);
      return;
    }
    removeCompany(companyId);
  };

  const addCompany = (companyId: number) => {
    setCompaniesToMerge(new Set(companiesToMerge).add(companyId));
  };

  const removeCompany = (companyId: number) => {
    const updatedCompanies = new Set(companiesToMerge);
    updatedCompanies.delete(companyId);
    setCompaniesToMerge(updatedCompanies);
  };

  return (
    <>
      {isMobile ? (
        <Box sx={{ opacity: isRefreshing ? 0.5 : 1, transition: "opacity 120ms" }}>
          {!showSkeleton &&
            companiesSearchResults?.data.map((c) => {
              const branch = c.branches[0];
              const cityLine = [branch?.city, [branch?.state, branch?.zip].filter(Boolean).join(" ")]
                .filter(Boolean)
                .join(", ");
              const address = [branch?.address1, branch?.address2].filter(Boolean).join(" ");
              const isSelected = companiesToMerge.has(c.id);
              const isSelectDisabled = companiesToMerge.size >= maxCompaniesToMerge && !isSelected;
              const rowBody = (
                <>
                  <CustomAvatar
                    alt={c.name}
                    name={c.name.trim()}
                    src={getLogo(c?.website)}
                    sx={{ width: 36, height: 36 }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2">
                      <Highlighter
                        autoEscape
                        textToHighlight={c.name || ""}
                        highlightStyle={highlightStyles}
                        searchWords={searchWords}
                      />
                    </Typography>
                    {c.companyType?.name && (
                      <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                        {c.companyType.name}
                      </Typography>
                    )}
                    {(address || cityLine) && (
                      <Typography variant="caption" sx={{ color: "text.disabled", display: "block" }}>
                        <Highlighter
                          autoEscape
                          textToHighlight={[address, cityLine].filter(Boolean).join(", ")}
                          highlightStyle={highlightStyles}
                          searchWords={searchWords}
                        />
                      </Typography>
                    )}
                    {c.branches.length > 1 && (
                      <Typography variant="caption" sx={{ color: "text.disabled", display: "block" }}>
                        {c.branches.length} branches
                      </Typography>
                    )}
                  </Box>
                </>
              );
              return (
                <Box
                  key={c.id}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    px: 2,
                    py: 1.75,
                    borderBottom: "1px solid",
                    borderColor: "divider"
                  }}
                >
                  {/* Selection mode borrows the iOS Mail pattern: a circle appears on
                      the left and the whole row toggles instead of navigating. */}
                  {isSelectionMode && (
                    <Checkbox
                      disabled={isSelectDisabled}
                      checked={isSelected}
                      onChange={(_, isChecked) => handleCheckboxChange(c.id, isChecked)}
                      sx={{ alignSelf: "center", p: 0.5 }}
                    />
                  )}

                  {isSelectionMode ? (
                    <Box
                      onClick={() => {
                        if (!isSelectDisabled) {
                          handleCheckboxChange(c.id, !isSelected);
                        }
                      }}
                      sx={{ display: "flex", gap: 1.5, flex: 1, minWidth: 0, cursor: "pointer" }}
                    >
                      {rowBody}
                    </Box>
                  ) : (
                    <Link
                      to={`../phonebook/company/profile?id=${c.id}`}
                      style={{
                        textDecoration: "none",
                        color: "#212B36",
                        display: "flex",
                        gap: 12,
                        flex: 1,
                        minWidth: 0
                      }}
                      onClick={() => {
                        logEvent("Phonebook", "Clicked to view a company profile");
                      }}
                    >
                      {rowBody}
                    </Link>
                  )}

                  {/* Outside the profile link so these act on the number/site. */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ flexShrink: 0, display: isSelectionMode ? "none" : "flex" }}
                  >
                    {/* Slots stay put whether or not the company has the detail, so
                        the columns line up down the list. */}
                    <Box sx={{ width: 36, flexShrink: 0 }}>
                      {c.website ? (
                        <IconButton
                          aria-label={`Open ${c.name} website`}
                          href={`https://${c.website.replace(/^https?:\/\//, "")}`}
                          target="_blank"
                          sx={{ width: 36, height: 36, bgcolor: "grey.200", "&:hover": { bgcolor: "grey.300" } }}
                        >
                          <Iconify icon="eva:globe-outline" width={19} />
                        </IconButton>
                      ) : null}
                    </Box>
                    <Box sx={{ width: 36, flexShrink: 0 }}>
                      {c.phone ? (
                        <IconButton
                          aria-label={`Call ${c.name}`}
                          href={`tel:+${c.phoneCountryCode}${c.phone}`}
                          sx={{
                            width: 36,
                            height: 36,
                            color: "#fff",
                            backgroundColor: IOS_GREEN,
                            "&:hover": { backgroundColor: IOS_GREEN, filter: "brightness(0.92)" }
                          }}
                        >
                          <Iconify icon="eva:phone-fill" width={19} />
                        </IconButton>
                      ) : null}
                    </Box>
                  </Stack>
                </Box>
              );
            })}
        </Box>
      ) : (
        <StickyTable>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Website</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Address of HQ</TableCell>
              <TableCell>Branch Count</TableCell>
              <TableCell>Account Rep</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!showSkeleton &&
              companiesSearchResults?.data.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <Checkbox
                      disabled={companiesToMerge.size >= maxCompaniesToMerge && !companiesToMerge.has(c.id)}
                      checked={companiesToMerge.has(c.id)}
                      onChange={(_, isChecked) => handleCheckboxChange(c.id, isChecked)}
                    />
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`../phonebook/company/profile?id=${c.id}`}
                      style={{
                        textDecoration: "none",
                        color: "#212B36"
                      }}
                      onClick={() => {
                        logEvent("Phonebook", "Clicked to view a company profile");
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CustomAvatar alt={c.name} name={c.name.trim()} src={getLogo(c?.website)} />
                        <Box>
                          <Typography variant="subtitle2">
                            <Highlighter
                              autoEscape
                              textToHighlight={c.name || ""}
                              highlightStyle={highlightStyles}
                              searchWords={searchWords}
                            />
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontStyle: "italic"
                            }}
                          >
                            {c.companyType?.name}
                          </Typography>
                        </Box>
                      </Stack>
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      <MUILink color="inherit" href={`https://${c.website}`} target="_blank">
                        <Highlighter
                          autoEscape
                          textToHighlight={c.website?.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "") || ""}
                          highlightStyle={highlightStyles}
                          searchWords={searchWords}
                        />
                      </MUILink>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      <MUILink color="initial" href={`tel:+${c.phoneCountryCode}${c.phone}`}>
                        <Highlighter
                          autoEscape
                          textToHighlight={
                            c.phone
                              ? formatPhone({
                                  number: c.phone,
                                  countryCode: c.phoneCountryCode
                                } as Phone)
                              : ""
                          }
                          highlightStyle={highlightStyles}
                          searchWords={searchWords}
                        />
                      </MUILink>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      <Highlighter
                        autoEscape
                        textToHighlight={`${c.branches[0]?.address1 || ""} ${c.branches[0]?.address2 || ""}`}
                        highlightStyle={highlightStyles}
                        searchWords={searchWords}
                      />
                    </Typography>
                    <Typography variant="body2">
                      <Highlighter
                        autoEscape
                        textToHighlight={`${c.branches[0]?.city || ""}${(c.branches[0]?.city || "") && ","} ${
                          c.branches[0]?.state || ""
                        } ${c.branches[0]?.zip || ""}`}
                        highlightStyle={highlightStyles}
                        searchWords={searchWords}
                      />
                    </Typography>
                  </TableCell>
                  <TableCell>{c.branches.length}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              ))}
          </TableBody>
        </StickyTable>
      )}
      {showSkeleton && <LoadingSkeleton />}
      {companiesSearchResults?.data.length === 0 && (
        <Box textAlign="center" padding={3}>
          <Typography>Your search for "{searchData.searchTerm}" didn't return any results</Typography>
          <Typography variant="caption">Check for spelling mistakes</Typography>
        </Box>
      )}
      <TablePagination
        // The label costs more width than it earns on a phone.
        labelRowsPerPage={isMobile ? "" : "Rows per page:"}
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={companiesSearchResults?.count || 0}
        rowsPerPage={searchData.pageSize}
        page={searchData.pageNumber}
        onPageChange={(e, number) => handlePageNumberChange(number)}
        onRowsPerPageChange={(e) => handleChangeRowsPerPage(e)}
      />
    </>
  );
}
